import React, { useState, useRef, useEffect } from "react";
import styles from "./ShopManagement.module.css";
import { useNavigate } from "react-router-dom";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { apiService } from "../../services/api";
import { Marquee } from "../../types";

export default function MarqueeManagement() {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const [isModalOpen, setIsModalOpen] = useState(false);
  const modalContentRef = useRef<HTMLDivElement>(null);
  const [editing, setEditing] = useState<Marquee | null>(null);
  const [form, setForm] = useState({ textRu: "", textEn: "", isActive: true });
  const [error, setError] = useState("");
  const [success, setSuccess] = useState("");

  const { data: marquees, isLoading } = useQuery({
    queryKey: ["marquees"],
    queryFn: () => apiService.getMarquees(),
  });

  const createMutation = useMutation({
    mutationFn: apiService.createMarquee,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["marquees"] });
      setIsModalOpen(false);
      resetForm();
      setSuccess("Запись успешно создана");
    },
    onError: (e: any) => {
      const validationErrors = e?.response?.data?.errors;
      if (validationErrors && Array.isArray(validationErrors) && validationErrors.length > 0) {
        const errorMessages = validationErrors.map((err: any) => {
          const fieldName = err.param || "Поле";
          const message = err.msg || "Ошибка валидации";
          return `• ${fieldName}: ${message}`;
        });
        setError(`Ошибки валидации:\n${errorMessages.join("\n")}`);
      } else {
        setError(e?.response?.data?.message || "Ошибка создания");
      }
    },
  });

  const updateMutation = useMutation({
    mutationFn: ({ id, data }: { id: string; data: Partial<Marquee> }) =>
      apiService.updateMarquee(id, data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["marquees"] });
      setIsModalOpen(false);
      resetForm();
      setSuccess("Запись обновлена");
    },
    onError: (e: any) => {
      const validationErrors = e?.response?.data?.errors;
      if (validationErrors && Array.isArray(validationErrors) && validationErrors.length > 0) {
        const errorMessages = validationErrors.map((err: any) => {
          const fieldName = err.param || "Поле";
          const message = err.msg || "Ошибка валидации";
          return `• ${fieldName}: ${message}`;
        });
        setError(`Ошибки валидации:\n${errorMessages.join("\n")}`);
      } else {
        setError(e?.response?.data?.message || "Ошибка обновления");
      }
    },
  });

  const deleteMutation = useMutation({
    mutationFn: apiService.deleteMarquee,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["marquees"] });
      setSuccess("Запись удалена");
    },
    onError: (e: any) =>
      setError(e?.response?.data?.message || "Ошибка удаления"),
  });

  const resetForm = () => {
    setForm({ textRu: "", textEn: "", isActive: true });
    setEditing(null);
    setError("");
    setSuccess("");
  };

  const openNew = () => {
    resetForm();
    setIsModalOpen(true);
  };

  const openEdit = (m: Marquee) => {
    setEditing(m);
    setForm({
      textRu: m.textRu || "",
      textEn: m.textEn || "",
      isActive: m.isActive,
    });
    setIsModalOpen(true);
  };

  const onSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    setError("");

    const validationMessages: string[] = [];

    if (!form.textRu.trim() && !form.textEn.trim()) {
      validationMessages.push("• Текст: необходимо указать текст на русском или английском");
    }

    if (validationMessages.length > 0) {
      setError(`Ошибки валидации:\n${validationMessages.join("\n")}`);
      return;
    }
    if (editing) {
      updateMutation.mutate({ id: editing.id, data: form });
    } else {
      createMutation.mutate(form);
    }
  };

  const remove = (id: string) => {
    if (window.confirm("Удалить запись?")) deleteMutation.mutate(id);
  };

  useEffect(() => {
    if (isModalOpen && modalContentRef.current) {
      modalContentRef.current.scrollTop = 0;
    }
  }, [isModalOpen]);

  if (isLoading) {
    return (
      <div className={styles.container}>
        <div className={styles.loadingSpinner}>Загрузка бегущей строки...</div>
      </div>
    );
  }

  return (
    <div className={styles.container}>
      <header className={styles.header}>
        <div className={styles.logo}>Управление бегущей строкой</div>
        <button
          className={styles.backButton}
          onClick={() => navigate("/admin")}
        >
          Назад
        </button>
      </header>

      <main className={styles.mainContent}>
        <h1 className={styles.pageTitle}>Бегущая строка</h1>
        {error && (
          <div
            className={styles.errorMessage}
            style={{ whiteSpace: "pre-line" }}
          >
            {error}
          </div>
        )}
        {success && <div className={styles.successMessage}>{success}</div>}

        <button className={styles.addButton} onClick={openNew} type="button">
          + Добавить запись
        </button>

        <table className={styles.table}>
          <thead className={styles.tableHeader}>
            <tr>
              <th className={styles.tableHeaderCell}>Текст (RU)</th>
              <th className={styles.tableHeaderCell}>Текст (EN)</th>
              {/* <th className={styles.tableHeaderCell}>Статус</th> */}
              <th className={styles.tableHeaderCell}>Действия</th>
            </tr>
          </thead>
          <tbody>
            {marquees?.map((m) => (
              <tr key={m.id} className={styles.tableRow}>
                <td className={styles.tableCell}>{m.textRu || "—"}</td>
                <td className={styles.tableCell}>{m.textEn || "—"}</td>
                {/* <td className={styles.tableCell}>
                  <span className={`${styles.badge} ${m.isActive ? styles.badgeActive : styles.badgeInactive}`}>
                    {m.isActive ? "Активно" : "Неактивно"}
                  </span>
                </td> */}
                <td className={styles.tableCell}>
                  <button
                    className={styles.actionButton}
                    onClick={() => openEdit(m)}
                  >
                    Редактировать
                  </button>
                  {/* <button
                    className={`${styles.actionButton} ${styles.actionWarning}`}
                    onClick={() => toggleActive(m)}
                  >
                    {m.isActive ? "Деактивировать" : "Активировать"}
                  </button> */}
                  <button
                    className={`${styles.actionButton} ${styles.actionDanger}`}
                    onClick={() => remove(m.id)}
                  >
                    Удалить
                  </button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>

        <div
          className={`${styles.modal} ${isModalOpen ? styles.modalOpen : ""}`}
        >
          <div
            ref={modalContentRef}
            className={styles.modalContent}
            onClick={(e) => e.stopPropagation()}
          >
            <h2 className={styles.modalTitle}>
              {editing ? "Редактировать запись" : "Добавить запись"}
            </h2>
            <form onSubmit={onSubmit}>
              <div className={styles.formGroup}>
                <label className={styles.label}>Текст (RU)</label>
                <textarea
                  className={styles.textArea}
                  value={form.textRu}
                  onChange={(e) =>
                    setForm((p) => ({ ...p, textRu: e.target.value }))
                  }
                  placeholder="Текст на русском"
                  rows={3}
                />
              </div>
              <div className={styles.formGroup}>
                <label className={styles.label}>Текст (EN)</label>
                <textarea
                  className={styles.textArea}
                  value={form.textEn}
                  onChange={(e) =>
                    setForm((p) => ({ ...p, textEn: e.target.value }))
                  }
                  placeholder="Text in English"
                  rows={3}
                />
              </div>
              <div className={styles.formGroup}>
                <div className={styles.checkboxContainer}>
                  <input
                    className={styles.checkboxInput}
                    type="checkbox"
                    id="isActive"
                    checked={form.isActive}
                    onChange={(e) =>
                      setForm((p) => ({ ...p, isActive: e.target.checked }))
                    }
                  />
                  <label className={styles.checkboxLabel} htmlFor="isActive">
                    Активно
                  </label>
                </div>
              </div>
              <div className={styles.modalActions}>
                <button
                  className={`${styles.actionButton} ${styles.actionWarning}`}
                  type="button"
                  onClick={() => setIsModalOpen(false)}
                >
                  Отмена
                </button>
                <button
                  className={styles.actionButton}
                  type="submit"
                  disabled={
                    createMutation.isPending || updateMutation.isPending
                  }
                >
                  {createMutation.isPending || updateMutation.isPending
                    ? "Сохранение..."
                    : editing
                    ? "Обновить"
                    : "Создать"}
                </button>
              </div>
            </form>
          </div>
        </div>
      </main>
    </div>
  );
}
